import { gql } from 'apollo-angular';
import { ConsentStatus } from '../../../interfaces/consent-status';
import { ConsentRequestType } from '../../../interfaces/consent-request-type';
import { ConsentRequestRoleGroup } from '../../../interfaces/consent-request-role-group';
import { ConsentRequest } from '../../../interfaces/consent-request';
import { Paginator, PaginatorInfo } from '../../../types/paginator';
import { User } from '../../../interfaces/user';
import { Person } from '../../../interfaces/person';
import ID from '../../../types/id';

export type ConsentRequestFilterInput = {
  consent_status_id?: string | null;
  consent_request_type_id?: string | null;
  asset_manager_id?: string | null;
  disposition_associate_id?: string | null;
  underwriter_id?: string | null;
  fund_manager_id?: string | null;
  received_date?: string | null;
  project_id?: string | null;
};
export type ConsentRequestAssignees = {
  assetManagers: Person[];
  fundManagers: Person[];
  underwriters: Person[];
  dispositionAssociates: Person[];
  consentManagers: Person[];
  investorRelations: Person[];
};
type FetchConsentRequestsVariables = {
  page: number;
  excludeStatuses?: string[] | null;
};
type FetchConsentRequestResponse = {
  getConsentStatuses: ConsentStatus[];
  getConsentRequestTypes: ConsentRequestType[];
  getConsentRequestRoleGroups: ConsentRequestRoleGroup[];
  getConsentRequests: Paginator<ConsentRequest, PaginatorInfo>;
  getConsentRequestAssignees: ConsentRequestAssignees;
  me: User;
};
export type FetchConsentRequestsPageVariables = {
  page: number;
  excludeStatuses?: string[] | null;
  filter?: ConsentRequestFilterInput | null;
};
export type FetchConsentRequestPageResponse = {
  getConsentRequests: Paginator<ConsentRequest, PaginatorInfo>;
  getConsentRequestAssignees: ConsentRequestAssignees;
};

const CONSENT_REQUEST_FRAGMENT = gql`
  fragment ConsentRequestFields on ConsentRequestPaginator {
    data {
      id
      consent_request_type_id
      consent_status_id
      timing_date
      urgency_note
      is_urgent_flag
      received_date
      details
      hasPastDue
      allApprovalsApproved
      hasDeniedApprovals
      project {
        id
        name
        developer {
          id
          name
        }
        participants(withRole: ["Sponsor"]){
          person {
            id
            name
          }
          organization {
            id
            name
          }
          role {
            name
          }
          sponsor {
            id
            is_repeat
            name
            nickname
          }
        }
      }
      fundManager {
        id
        name
      }
      assetManager {
        id
        name
      }
      underwriter {
        id
        name
      }
      consentManager {
        id
        name
      }
      investorRelations {
        id
        name
      }
      dispositionAssociate {
        id
        name
      }
    }
    paginatorInfo {
      currentPage
      hasMorePages
      perPage
      lastPage
      total
    }
  }
`;

export const FETCH_CONSENT_REQUEST_PAGE = gql<
  FetchConsentRequestPageResponse,
  FetchConsentRequestsPageVariables
>`
  query fetchConsentReqestsPage(
    $page: Int
    $excludeStatuses: [String!]
    $filter: ConsentRequestFilterInput
  ) {
    getConsentRequests(
      page: $page
      excludeStatuses: $excludeStatuses
      filter: $filter
      order: [
        { column: IS_URGENT_FLAG, order: ASC }
        { column: TIMING_DATE, order: DESC }
        { column: RECEIVED_DATE, order: DESC }
      ]
    ) {
      ...ConsentRequestFields
    }

    getConsentRequestAssignees {
      assetManagers {
        id
        name
      }
      fundManagers {
        id
        name
      }
      investorRelations {
        id
        name
      }
      consentManagers {
        id
        name
      }
      underwriters {
        id
        name
      }
      dispositionAssociates {
        id
        name
      }
    }
  }
  ${CONSENT_REQUEST_FRAGMENT}
`;
export const FETCH_CONSENT_REQUESTS = gql<
  FetchConsentRequestResponse,
  FetchConsentRequestsVariables
>`
  query fetchConsentRequests($page: Int, $excludeStatuses: [String!]) {
    getConsentStatuses {
      id
      status
      sort_index
    }
    getConsentRequestAssignees {
      assetManagers {
        id
        name
      }
      fundManagers {
        id
        name
      }
      investorRelations {
        id
        name
      }
      consentManagers {
        id
        name
      }
      underwriters {
        id
        name
      }
      dispositionAssociates {
        id
        name
      }
    }
    getConsentRequestRoleGroups {
      id
      role_column_name
      group_id
    }
    getConsentRequestTypes {
      id
      request_type
      fundManager {
        id
        name
      }
      assetManager {
        id
        name
      }
      underwriter {
        id
        name
      }
      consentManager {
        id
        name
      }
      investorRelations {
        id
        name
      }
      dispositionAssociate {
        id
        name
      }
    }
    getConsentRequests(
      page: $page
      excludeStatuses: $excludeStatuses
      order: [
        { column: IS_URGENT_FLAG, order: ASC }
        { column: TIMING_DATE, order: DESC }
        { column: RECEIVED_DATE, order: DESC }
      ]
    ) {
      ...ConsentRequestFields
    }
    me {
      permissionsForResource(resourceId: null, resourceType: CONSENTREQUEST) {
        _create
        _edit
        _delete
        _read
        authorized_fields
      }
    }
  }
  ${CONSENT_REQUEST_FRAGMENT}
`;
export type UpdateConsentRequestInput = {
  asset_manager_id: string | null;
  fund_manager_id: string | null;
  underwriter_id: string | null;
  consent_manager_id: string | null;
  investor_relations_id: string | null;
  disposition_associate_id: string | null;
  details: string | null;
  received_date: string | null;
  timing_date: string | null;
  urgency_note: string | null;
  is_urgent_flag: boolean;
};

export type CreateConsentRequestInput = UpdateConsentRequestInput & {
  project_id: string;
  consent_request_type_id: string;
};
type CreateConsentRequestResult = {
  createConsentRequest: ConsentRequest;
};
type CreateConsentRequestVariables = {
  input: CreateConsentRequestInput;
};

export const CREATE_CONSENT_REQUEST = gql<
  CreateConsentRequestResult,
  CreateConsentRequestVariables
>`
  mutation createConsentRequest($input: CreateConsentRequestInput) {
    createConsentRequest(input: $input) {
      id
    }
  }
`;

type UpdateConsentRequestVariables = {
  id: ID;
  input: UpdateConsentRequestInput;
};
type UpdateConsentRequestResponse = {
  updateConsentRequest: ConsentRequest;
};
export const UPDATE_CONSENT_REQUEST = gql<
  UpdateConsentRequestResponse,
  UpdateConsentRequestVariables
>`
  mutation updateConsentRequest($id: ID!, $input: UpdateConsentRequestInput) {
    updateConsentRequest(id: $id, input: $input) {
      id
    }
  }
`;
export const DELETE_CONSENT_REQUEST = gql`
  mutation deleteConsentRequest($id: ID!) {
    deleteConsentRequest(id: $id) {
      id
    }
  }
`;
